import {
  Box,
  Drawer,
  IconButton,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Typography,
  Avatar,
} from '@mui/material';
import MenuIcon from '@mui/icons-material/Menu';
import AvatarInitials from '../user/AvatarInitials';
import { Link, useNavigate } from 'react-router-dom';
import { useEffect, useState } from 'react';
import { AUTHENTICATED, UNAUTHENTICATED } from '../../constants/user';
import { useThemeContext } from '../../contexts/BaseThemeContextProvider';
import { fetchCognitoUserToken } from '../../utils/user';
import PrimaryBlueButton from '../common/buttons/PrimaryBlueButton';
import BorderedButton from '../common/buttons/BorderedButton';
import LightModeIcon from '@mui/icons-material/LightMode';

const backendURL = process.env.REACT_APP_BACKEND_URL;
const productPageURL: string = "" + process.env.REACT_APP_PRODUCT_PAGE_URL;

type DrawerMobileMenuProps = {
  fullName: string;
  email: string;
  userType: string;
};

export default function DrawerMobileMenu({ fullName, userType, email }: DrawerMobileMenuProps) {
  const navigate = useNavigate();
  const [mobileOpen, setMobileOpen] = useState(false);
  const isGuestUser = userType === UNAUTHENTICATED;
  const { handleLogOut, theme } = useThemeContext();
  const [notificationCount, setNotificationCount] = useState(null);

  const navigateAndCloseDrawer = (path: string) => {
    navigate(path);
    setMobileOpen(false);
  };

  const handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen);
  };

  const listItemTextStyle = {
    color: theme.custom.drawerMobileMenu.listItemTextStyle.color,
    fontSize: '14px',
    fontWeight: 400,
    lineHeight: '20px',
    letterSpacing: '0.25px',
  };

  const notificationCountStyle = {
    color: theme.custom.drawerMobileMenu.notificationCountStyle.color,
    fontSize: '14px',
    fontWeight: 500,
    lineHeight: '20px',
    letterSpacing: '0.25px',
    marginLeft: 'auto',
  };

  useEffect(() => {
    const fetchNotificationCount = async () => {
      if (userType !== AUTHENTICATED) {
        return;
      }

      let access_token = await fetchCognitoUserToken();
      try {
        const res = await fetch(`${backendURL}/messages/get_unread_count`, {
          method: 'GET',
          headers: new Headers({
            Authorization: 'Bearer ' + access_token,
          }),
        });
        if (res.ok) {
          const data = await res.json();
          setNotificationCount(data.count);
        } else {
          console.log('Failed to fetch notification count.');
        }
      } catch (error) {
        console.log('Failed to fetch notification count', error);
      }
    };

    fetchNotificationCount();
  }, [userType]);

  const mainDrawer = (
    <Box
      sx={{
        width: 350,
        background: theme.custom.drawerMobileMenu.background,
        height: '100vh',
        display: 'flex',
        flexDirection: 'column',
      }}
      role="presentation"
    >
      {/* Header section */}
      <Box
        sx={{
          paddingLeft: '16px',
          paddingTop: '8px',
          paddingBottom: '8px',
          marginTop: '12px',
          display: 'flex-start',
          alignItems: 'center',
          flexDirection: 'column',
        }}
      >
        <Link
          to="https://www.relentlesshealth.com/"
          style={{
            display: 'flex',
            flexDirection: 'column',
            textDecoration: 'none',
            height: '100%',
          }}
        >
          <img src={theme.images.logo.src} alt="Logo" style={{ width: 175, marginBottom: 16 }} />
        </Link>
      </Box>
      {/* Avatar and menu section for Auth users*/}
      {!isGuestUser && (
        <>
          <Box
            sx={{ paddingLeft: '16px', marginTop: '12px', marginBottom: '16px', display: 'flex', alignItems: 'center' }}
          >
            <Avatar sx={{ width: 40, height: 40, marginRight: '10px' }}>
              <AvatarInitials fullName={fullName} />
            </Avatar>
            <Box sx={{ display: 'flex', flexDirection: 'column' }}>
              <Typography
                variant="body1"
                sx={{ fontSize: '16px', fontWeight: 500, color: theme.custom.drawerMobileMenu.avatar.fullName.color }}
              >
                {fullName}
              </Typography>
              <Typography
                variant="body2"
                sx={{ fontSize: '12px', fontWeight: 400, color: theme.custom.drawerMobileMenu.avatar.email.color }}
              >
                {email}
              </Typography>
            </Box>
          </Box>
          <List>
            <ListItem key={'profile'} disablePadding>
              <ListItemButton onClick={() => navigateAndCloseDrawer('/users/edit.html')}>
                <ListItemIcon sx={{ minWidth: 'auto', marginRight: '12px' }}>
                  <img src={theme.custom.drawerMobileMenu.icons.profile} alt="profile" />
                </ListItemIcon>
                <ListItemText sx={listItemTextStyle} primary={'Edit profile'} />
              </ListItemButton>
            </ListItem>

            <ListItem key={'messages'} disablePadding>
              <ListItemButton onClick={() => navigateAndCloseDrawer('/inbox.html')}>
                <ListItemIcon sx={{ minWidth: 'auto', marginRight: '12px' }}>
                  <img src={theme.custom.drawerMobileMenu.icons.messages} alt="Messages" />
                </ListItemIcon>
                <ListItemText sx={listItemTextStyle} primary={'Messages'} />
                {notificationCount !== null && notificationCount > 0 && (
                  <Typography sx={notificationCountStyle}>{notificationCount}</Typography>
                )}
              </ListItemButton>
            </ListItem>
            <ListItem key={'appearance'} disablePadding>
              <ListItemButton onClick={() => navigateAndCloseDrawer('/appearance.html')}>
                <LightModeIcon
                  sx={{ color: theme.profile.leftMenu.appearance.activeColor, minWidth: 'auto', marginRight: '12px' }}
                />
                <ListItemText sx={listItemTextStyle} primary={'Appearance'} />
              </ListItemButton>
            </ListItem>
            <ListItem key={'Results'} disablePadding>
              <ListItemButton onClick={() => navigateAndCloseDrawer('/results.html')}>
                <ListItemIcon sx={{ minWidth: 'auto', marginRight: '12px' }}>
                  <img src={theme.custom.drawerMobileMenu.icons.results} alt="Results" />
                </ListItemIcon>
                <ListItemText sx={listItemTextStyle} primary={'View your results'} />
              </ListItemButton>
            </ListItem>
            <ListItem key={'view-orders'} disablePadding>
              <ListItemButton onClick={() => navigateAndCloseDrawer('/orders.html')}>
                <ListItemIcon sx={{ minWidth: 'auto', marginRight: '12px' }}>
                  <img src={theme.custom.drawerMobileMenu.icons.orders} alt="View orders" />
                </ListItemIcon>
                <ListItemText sx={listItemTextStyle} primary={'View your orders'} />
              </ListItemButton>
            </ListItem>
          </List>
        </>
      )}

      <List>
        <ListItem key={'order-test'} disablePadding>
          <ListItemButton href={productPageURL} >
            <ListItemIcon sx={{ minWidth: 'auto', marginRight: '12px' }}>
              <img src={theme.custom.drawerMobileMenu.icons.order} alt="order" />
            </ListItemIcon>
            <ListItemText sx={listItemTextStyle} primary={'Buy a test'} />
          </ListItemButton>
        </ListItem>
        <ListItem key={'activate-test'} disablePadding>
          <ListItemButton onClick={() => navigateAndCloseDrawer('/activate.html')}>
            <ListItemIcon sx={{ minWidth: 'auto', marginRight: '12px' }}>
              <img src={theme.custom.drawerMobileMenu.icons.activate} alt="Activate test" />
            </ListItemIcon>
            <ListItemText sx={listItemTextStyle} primary={'Activate a test'} />
          </ListItemButton>
        </ListItem>
        <ListItem key={'Support'} disablePadding>
          <ListItemButton onClick={() => navigateAndCloseDrawer('/support.html')}>
            <ListItemIcon sx={{ minWidth: 'auto', marginRight: '12px' }}>
              <img src={theme.custom.drawerMobileMenu.icons.support} alt="Support" />
            </ListItemIcon>
            <ListItemText sx={listItemTextStyle} primary={'Support'} />
          </ListItemButton>
        </ListItem>

        <ListItem key={'privacy-policy'} disablePadding>
          <ListItemButton onClick={() => navigateAndCloseDrawer('/privacy-policy.html')}>
            <ListItemIcon sx={{ minWidth: 'auto', marginRight: '12px' }}>
              <img src={theme.custom.drawerMobileMenu.icons.privacyPolicy} alt="Privacy Policy" />
            </ListItemIcon>
            <ListItemText sx={listItemTextStyle} primary={'Privacy policy'} />
          </ListItemButton>
        </ListItem>

        <ListItem key={'terms-of-service'} disablePadding>
          <ListItemButton onClick={() => navigateAndCloseDrawer('/tos.html')}>
            <ListItemIcon sx={{ minWidth: 'auto', marginRight: '12px' }}>
              <img src={theme.custom.drawerMobileMenu.icons.termsOfService} alt="Terms of service" />
            </ListItemIcon>
            <ListItemText sx={listItemTextStyle} primary={'Terms of service'} />
          </ListItemButton>
        </ListItem>
      </List>
      {!isGuestUser && (
        <Box sx={{ mt: 'auto', marginBottom: '8px', marginTop: '8px' }}>
          <List>
            <ListItem key={'logout'} disablePadding>
              <ListItemButton onClick={() => handleLogOut()}>
                <ListItemIcon sx={{ minWidth: 'auto', marginRight: '12px' }}>
                  <img src={theme.custom.drawerMobileMenu.icons.logout} alt="log out" />
                </ListItemIcon>
                <ListItemText sx={listItemTextStyle} primary={'Log out'} />
              </ListItemButton>
            </ListItem>
          </List>
        </Box>
      )}

      {isGuestUser && (
        <List>
          <ListItem key={'Sign In'} disablePadding>
            <PrimaryBlueButton
              onClick={() => navigate('/login')}
              buttonText="Sign In"
              sxProps={{
                height: { xs: 48 },
                width: { xs: 232 },
                fontSize: '0.875rem',
                padding: { md: '8px 24px', xs: '2px 6px' },
                margin: 'auto',
              }}
            />
          </ListItem>
        </List>
      )}
    </Box>
  );

  return (
    <Box sx={{ marginRight: '-11px' }}>
      <IconButton color="inherit" aria-label="open drawer" edge="start" onClick={handleDrawerToggle}>
        {!isGuestUser ? (
          <AvatarInitials fullName={fullName} />
        ) : (
          <MenuIcon sx={{ fontSize: 40, color: theme.custom.drawerMobileMenu.iconButton.color }} />
        )}
      </IconButton>
      <Drawer anchor="right" open={mobileOpen} onClose={handleDrawerToggle}>
        {mainDrawer}
      </Drawer>
    </Box>
  );
}
